import { useCallback, useEffect, useRef, useState } from 'react';
import { documentsAPI } from '../services/api';
import { openBlob, readBlobText, saveBlob } from '../components/documents/documentFiles';
import {
  CatalogItem,
  ParentAdmissionDocument,
  ParentDocument,
  ParentRequestSummary,
  ParentRequestView,
  RequestAttachment,
} from '../types';
import { extractDocumentError, safeFileName, safePdfFileName } from '../utils/documents';
import { Logger } from '../utils/logger';

// Data and file actions for the Documents screens, shared by native and web. The list
// hooks guard against a slow answer for a previous child landing after the parent has
// switched to another one.

export function useDocumentCatalog(studentId?: string) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      const ticket = ++latest.current;
      if (!studentId) {
        setItems([]);
        setLoading(false);
        return;
      }
      if (mode === 'refresh') setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const { data } = await documentsAPI.getCatalog(studentId);
        if (ticket !== latest.current) return;
        setItems(data ?? []);
      } catch (err) {
        if (ticket !== latest.current) return;
        Logger.error('Documents', 'Failed to load the document catalogue', err);
        setError(extractDocumentError(err, 'Could not load the documents you can request.').message);
      } finally {
        if (ticket === latest.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [studentId],
  );

  useEffect(() => {
    setItems([]);
    load();
  }, [load]);

  return { items, loading, refreshing, error, reload: load };
}

const PAGE_SIZE = 20;

export function useDocumentRequests(studentId: string | undefined, view: ParentRequestView) {
  const [items, setItems] = useState<ParentRequestSummary[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      const ticket = ++latest.current;
      if (!studentId) {
        setItems([]);
        setLoading(false);
        return;
      }
      if (mode === 'refresh') setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const { data } = await documentsAPI.getRequests({ studentId, view, page: 1, pageSize: PAGE_SIZE });
        if (ticket !== latest.current) return;
        setItems(data?.items ?? []);
        setCounts(data?.counts ?? {});
        setPage(data?.page ?? 1);
        setTotalPages(data?.totalPages ?? 1);
      } catch (err) {
        if (ticket !== latest.current) return;
        Logger.error('Documents', 'Failed to load document requests', err);
        setError(extractDocumentError(err, 'Could not load your requests.').message);
      } finally {
        if (ticket === latest.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [studentId, view],
  );

  const loadMore = useCallback(async () => {
    if (!studentId || loadingMore || loading || page >= totalPages) return;
    const ticket = latest.current;
    setLoadingMore(true);
    try {
      const { data } = await documentsAPI.getRequests({ studentId, view, page: page + 1, pageSize: PAGE_SIZE });
      if (ticket !== latest.current) return;
      setItems(prev => {
        const seen = new Set(prev.map(item => item.id));
        return [...prev, ...(data?.items ?? []).filter(item => !seen.has(item.id))];
      });
      setCounts(data?.counts ?? {});
      setPage(data?.page ?? page + 1);
      setTotalPages(data?.totalPages ?? totalPages);
    } catch (err) {
      Logger.error('Documents', 'Failed to load more document requests', err);
      setError(extractDocumentError(err, 'Could not load more requests.').message);
    } finally {
      setLoadingMore(false);
    }
  }, [studentId, view, page, totalPages, loading, loadingMore]);

  useEffect(() => {
    setItems([]);
    load();
  }, [load]);

  return {
    items,
    counts,
    loading,
    refreshing,
    loadingMore,
    hasMore: page < totalPages,
    error,
    reload: load,
    loadMore,
  };
}

/** The family document locker: every child's issued documents, filtered client-side. */
/**
 * The family locker: documents uploaded at admission and documents the school issued. The two
 * lists load independently, so a problem with one never hides the other.
 */
export function useDocumentLocker() {
  const [items, setItems] = useState<ParentDocument[]>([]);
  const [admissionItems, setAdmissionItems] = useState<ParentAdmissionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [admissionError, setAdmissionError] = useState<string | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (mode === 'refresh') setRefreshing(true);
    else setLoading(true);
    setError(null);
    setAdmissionError(null);

    const [issued, admission] = await Promise.allSettled([
      documentsAPI.getDocuments(),
      documentsAPI.getAdmissionDocuments(),
    ]);
    if (issued.status === 'fulfilled') setItems(issued.value.data ?? []);
    else {
      Logger.error('Documents', 'Failed to load issued documents', issued.reason);
      setError(extractDocumentError(issued.reason, 'Could not load the documents issued by the school.').message);
    }
    if (admission.status === 'fulfilled') setAdmissionItems(admission.value.data ?? []);
    else {
      Logger.error('Documents', 'Failed to load admission documents', admission.reason);
      setAdmissionError(extractDocumentError(admission.reason, 'Could not load your admission documents.').message);
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { items, admissionItems, loading, refreshing, error, admissionError, reload: load };
}

// ── Files: issued PDFs and request attachments ─────────────────────────────

type Notify = (message: string, type: 'success' | 'error') => void;

interface IssuedDocumentRef {
  /** The generated document's id. */
  id: string;
  documentName: string;
  documentNumber?: string | null;
}

function toBlob(response: { data: any; headers?: any }, fallbackType: string): { blob: Blob; type: string } {
  const type = (response.headers?.['content-type'] as string) || fallbackType;
  const blob: Blob =
    response.data instanceof Blob ? response.data : new Blob([response.data], { type } as any);
  return { blob, type: blob.type || type };
}

/** A blob request's error body is itself a Blob, so it is read back before extracting the message. */
async function describeBlobError(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const text = await readBlobText(data);
    try {
      return extractDocumentError({ response: { ...err.response, data: JSON.parse(text) } }, fallback).message;
    } catch {
      return text.trim() && text.length < 300 ? text.trim() : fallback;
    }
  }
  return extractDocumentError(err, fallback).message;
}

/**
 * Downloading / opening the issued PDF and request attachments, with a busy key
 * (`save:<id>`, `open:<id>`, `attachment:<id>`) so the pressed button can show a
 * spinner. `onDelivered` lets a list refresh, since the first download moves the
 * request to DELIVERED. The platform file helpers come from documentFiles(.web).ts.
 */
export function useDocumentFileActions(notify: Notify, onDelivered?: () => void) {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const issuedDocument = useCallback(
    async (doc: IssuedDocumentRef, mode: 'save' | 'open') => {
      setBusyKey(`${mode}:${doc.id}`);
      try {
        const response = await documentsAPI.download(doc.id);
        const { blob, type } = toBlob(response, 'application/pdf');
        const name = safePdfFileName(doc.documentName, doc.documentNumber);
        const message = mode === 'save' ? await saveBlob(blob, name, type) : await openBlob(blob, name, type);
        if (mode === 'save') notify(message, 'success');
        onDelivered?.();
      } catch (err) {
        Logger.error('Documents', 'Failed to fetch the issued document', err);
        notify(await describeBlobError(err, 'Could not download the document. Please try again.'), 'error');
      } finally {
        setBusyKey(null);
      }
    },
    [notify, onDelivered],
  );

  const attachment = useCallback(
    async (requestId: string, file: RequestAttachment) => {
      setBusyKey(`attachment:${file.id}`);
      try {
        const response = await documentsAPI.getAttachment(requestId, file.id);
        const { blob, type } = toBlob(response, file.contentType || 'application/octet-stream');
        await openBlob(blob, safeFileName(file.fileName), type);
      } catch (err) {
        Logger.error('Documents', 'Failed to open attachment', err);
        notify(await describeBlobError(err, 'Could not open the attachment.'), 'error');
      } finally {
        setBusyKey(null);
      }
    },
    [notify],
  );

  const admissionDocument = useCallback(
    async (doc: ParentAdmissionDocument, mode: 'save' | 'open') => {
      setBusyKey(`${mode}:${doc.id}`);
      try {
        const response = await documentsAPI.downloadAdmissionDocument(doc.id);
        const { blob, type } = toBlob(response, 'application/octet-stream');
        const name = safeFileName(`${doc.documentName}.${doc.fileType.toLowerCase() || 'pdf'}`, 'document');
        const message = mode === 'save' ? await saveBlob(blob, name, type) : await openBlob(blob, name, type);
        if (mode === 'save') notify(message, 'success');
      } catch (err) {
        Logger.error('Documents', 'Failed to fetch the admission document', err);
        notify(await describeBlobError(err, 'Could not open the document. Please try again.'), 'error');
      } finally {
        setBusyKey(null);
      }
    },
    [notify],
  );

  return { busyKey, issuedDocument, admissionDocument, attachment };
}
